import { PropsWithChildren, ReactElement } from 'react';
import { Field, FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import Grid from '@mui/material/Grid';
import { Box, Fab, IconButton, FormLabel } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import classNames from 'classnames';
import AddIcon from '@mui/icons-material/Add';

// Components
import { FormikInputField } from 'components/ContinuousImprovement/PureFormElements';
import DatePicker from 'components/DatePicker/DatePicker';

// Styles
import styles from 'components/TextboxRepeater/TextboxRepeater.module.scss';
import get from 'lodash/get';
import { IBCTRemsRegularExpression } from 'layouts/pages/bct/types';

// Types
type IProps = PropsWithChildren<{
  disabled?: boolean;
  formikKey: string;
}>;

export const RemsRegularExpressionsRepeater = (props: IProps): ReactElement => {
  const { formikKey, disabled } = props;
  const { setFieldTouched, values } = useFormikContext();
  const formikAccess: IBCTRemsRegularExpression[] = get(values, formikKey);

  return (
    <FieldArray
      name={formikKey}
      render={(arrayHelpers: FieldArrayRenderProps): ReactElement => {
        return (
          <Grid container>
            {formikAccess?.map((_: IBCTRemsRegularExpression, idx: number): ReactElement => {
              return (
                <Grid container spacing={3} key={idx}>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <FormikInputField
                      label="Regex Label"
                      formikKey={`${formikKey}.${idx}.label`}
                      placeholder="Regex Label"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <FormikInputField
                      formikKey={`${formikKey}.${idx}.regularExpression`}
                      label="Regular Expression"
                      placeholder="Regex"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <FormLabel component="div">Start Date</FormLabel>
                    <Field name={`${formikKey}.${idx}.startDate`}>
                      {({ field }) => (
                        <DatePicker field={field} formikKey={`${formikKey}.${idx}.startDate`} timeOfDay="midnight" />
                      )}
                    </Field>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <FormLabel component="div">End Date</FormLabel>
                    <Field name={`${formikKey}.${idx}.endDate`}>
                      {({ field }) => (
                        <DatePicker field={field} formikKey={`${formikKey}.${idx}.endDate`} timeOfDay="endOfDay" />
                      )}
                    </Field>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <FormikInputField
                      formikKey={`${formikKey}.${idx}.errorMessage`}
                      label="Error Message"
                      placeholder="Error message"
                    />
                  </Grid>
                  <Box component={Grid} sx={{ alignSelf: 'center', mt: 3 }}>
                    <IconButton aria-label="remove" onClick={(): void => arrayHelpers.remove(idx)} size="small">
                      <ClearIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                </Grid>
              );
            })}
            <Box sx={{ display: 'flex', pt: 2, pb: 2 }}>
              <Box sx={{ mr: 2 }}>
                <Fab
                  aria-label="add another instance"
                  className={classNames(
                    {
                      [styles.disabled]: disabled,
                    },
                    styles['add-btn'],
                  )}
                  disabled={disabled}
                  onClick={(): void => {
                    arrayHelpers.push('');
                    setFieldTouched(formikKey, true);
                  }}
                  size="small"
                >
                  <AddIcon color="inherit" />
                </Fab>
              </Box>
              <Box sx={{ pt: 0.5 }}>Add Another Instance</Box>
            </Box>
          </Grid>
        );
      }}
    />
  );
};
